
import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-3.5 sm:[&_svg]:size-4 [&_svg]:shrink-0 relative overflow-hidden z-0 transition-all duration-300",
  {
    variants: {
      variant: {
        default: "bg-yellow-500 text-darkgray hover:bg-gradient-to-r hover:from-yellow-500 hover:to-yellow-300 before:absolute before:inset-x-0 before:bottom-0 before:h-0 before:bg-yellow-300 before:-z-10 before:transition-all before:duration-300 hover:before:h-full",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90 before:absolute before:inset-x-0 before:bottom-0 before:h-0 before:bg-destructive/70 before:-z-10 before:transition-all before:duration-300 hover:before:h-full",
        outline:
          "border-2 border-yellow-500 bg-transparent text-darkgray hover:bg-yellow-300/10 hover:text-darkgray before:absolute before:inset-x-0 before:bottom-0 before:h-0 before:bg-yellow-300/10 before:-z-10 before:transition-all before:duration-300 hover:before:h-full",
        secondary:
          "bg-darkgray text-white hover:bg-darkgray/90 before:absolute before:inset-x-0 before:bottom-0 before:h-0 before:bg-gray-700 before:-z-10 before:transition-all before:duration-300 hover:before:h-full",
        ghost: "bg-transparent text-darkgray hover:bg-yellow-300/10 hover:text-accent before:absolute before:inset-x-0 before:bottom-0 before:h-0 before:bg-yellow-300/10 before:-z-10 before:transition-all before:duration-300 hover:before:h-full",
        link: "text-accent underline-offset-4 hover:underline",
      },
      size: {
        default: "h-9 sm:h-10 px-3 sm:px-4 py-2 text-sm sm:text-base",
        sm: "h-8 sm:h-9 rounded-md px-2.5 sm:px-3 text-xs sm:text-sm",
        lg: "h-10 sm:h-11 rounded-md px-6 sm:px-8 text-base sm:text-lg",
        icon: "h-9 w-9 sm:h-10 sm:w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
